import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, TextField, FormControl, InputLabel,
  Select, MenuItem, Alert, Snackbar, CircularProgress, Box,
} from '@mui/material';
import useIsMobile from '../hooks/useIsMobile';
import { InstanceCreee, ModeleProposable, messageErreurs, codeHttp } from '../services/formulairesServices';
import { useApiFormulaires } from '../formulaires/ApiFormulairesContext';

// « Joindre un formulaire » à un dossier créé manuellement (lot 5) : choix d'un modèle ACTIF, e-mail du client
// pré-rempli (modifiable), puis POST /formulaires/dossiers → instance `cree` (aucun mail à ce stade). Le parent
// (FormulairesDossier) reçoit l'instance via onCree, ferme ce Dialog, recharge sa liste et prépare le composeur.
// Erreurs : 403 (dossier non manuel) → « Réservé aux dossiers créés manuellement » ; sinon messageErreurs.
// Appels par ApiFormulairesContext (admin par défaut, Legrand dans l'admin Legrand).

export interface JoindreFormulaireDialogProps {
  open: boolean;
  chemin: string;                            // chemin du dossier (demandes.chemin)
  emailPropose: string;                      // e-mail du dossier (dossier.email), pré-rempli à chaque ouverture
  onClose: () => void;
  onCree: (instance: InstanceCreee) => void; // 201 de POST /formulaires/dossiers
}

// Regex e-mail partagée avec les backends (§11 n°5 du plan)
const REGEX_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const JoindreFormulaireDialog: React.FC<JoindreFormulaireDialogProps> = ({ open, chemin, emailPropose, onClose, onCree }) => {
  const isMobile = useIsMobile();
  const api = useApiFormulaires();
  const [modeles, setModeles] = useState<ModeleProposable[] | null>(null); // null = chargement en cours
  const [idModele, setIdModele] = useState('');
  const [email, setEmail] = useState('');
  const [creation, setCreation] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null); // Alert persistante dans le Dialog
  const [toast, setToast] = useState<string | null>(null);   // même message en Snackbar

  // Dernier e-mail proposé, lu à l'ouverture sans figurer dans les dépendances : la saisie n'est pas écrasée par un
  // simple re-rendu du parent.
  const emailProposeRef = useRef(emailPropose);
  emailProposeRef.current = emailPropose;

  useEffect(() => {
    if (!open) return undefined;
    let actif = true;
    setIdModele('');
    setEmail(emailProposeRef.current || '');
    setErreur(null);
    setToast(null);
    setCreation(false);
    setModeles(null);
    api.listerModelesActifs()
      .then((liste) => {
        if (!actif) return;
        setModeles(liste);
        if (liste.length === 1) setIdModele(String(liste[0].id)); // un seul modèle actif : présélectionné
      })
      .catch((e) => {
        if (!actif) return;
        console.error('Erreur chargement modèles actifs', e);
        setModeles([]);
        setErreur(messageErreurs(e, 'Chargement des modèles impossible'));
      });
    return () => { actif = false; };
  }, [open, api]);

  const emailValide = REGEX_EMAIL.test(email.trim());
  const peutCreer = !!idModele && emailValide && !creation && !!modeles && modeles.length > 0;

  const signaler = (message: string) => { setErreur(message); setToast(message); };

  const creer = async () => {
    if (!peutCreer) return;
    setCreation(true);
    setErreur(null);
    try {
      const instance = await api.creerInstance({ chemin, id_modele: Number(idModele), email: email.trim() });
      onCree(instance);
    } catch (e) {
      console.error('Erreur création du formulaire joint', e);
      signaler(codeHttp(e) === 403
        ? 'Réservé aux dossiers créés manuellement'
        : messageErreurs(e, 'Création du formulaire impossible'));
    } finally {
      setCreation(false);
    }
  };

  // Pas de fermeture (fond, Échap, Annuler) pendant la création
  const fermer = () => { if (!creation) onClose(); };

  return (
    <>
      <Dialog open={open} onClose={fermer} fullWidth maxWidth="sm" fullScreen={isMobile}>
        <DialogTitle>Joindre un formulaire</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Le client recevra par mail un lien pour remplir ce formulaire en ligne. Le mail est à valider dans le
            composeur après la création.
          </DialogContentText>

          {erreur && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErreur(null)}>{erreur}</Alert>}

          {modeles === null ? (
            <Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress size={28} /></Box>
          ) : modeles.length === 0 ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              Aucun modèle de formulaire actif : créez-en un (ou activez-en un) dans le menu Formulaires.
            </Alert>
          ) : (
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel id="joindre-modele-label">Formulaire</InputLabel>
              <Select
                labelId="joindre-modele-label"
                label="Formulaire"
                value={idModele}
                onChange={(e) => setIdModele(String(e.target.value))}
                disabled={creation}
              >
                {modeles.map((m) => (
                  <MenuItem key={m.id} value={String(m.id)}>
                    {m.nom} (v{m.version}, {m.nb_questions} question{m.nb_questions > 1 ? 's' : ''})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <TextField
            label="E-mail du client"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={email.trim() !== '' && !emailValide}
            helperText={email.trim() !== '' && !emailValide ? 'Adresse e-mail invalide' : 'Destinataire du lien (modifiable)'}
            disabled={creation}
            fullWidth
            size="small"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={fermer} disabled={creation}>Annuler</Button>
          <Button
            variant="contained"
            onClick={creer}
            disabled={!peutCreer}
            startIcon={creation ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {creation ? 'Création…' : 'Créer et composer le mail'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={6000}
        onClose={(_e, raison) => { if (raison !== 'clickaway') setToast(null); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? <Alert severity="error" onClose={() => setToast(null)}>{toast}</Alert> : undefined}
      </Snackbar>
    </>
  );
};

export default JoindreFormulaireDialog;
